import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import App from './app/app';
import { Auth0ProviderWithHookInterceptor } from './components/Auth0ProviderWithHookInterceptor';
import ChunkLoadErrorBoundary from './components/ChunkLoadErrorBoundary';
import { VersionUpdateNotification } from './components/VersionUpdateNotification';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import {
  createStore,
  setAgentsStreamConfig,
  setAgenticAnalystConfig,
  setApiConfig,
  setVoiceAgentConfig,
} from '@circuitry-ai/doc-data';
import './i18n';
import { Userpilot } from 'userpilot';
import { versionCheckService } from './utils/versionCheckService';

if (import.meta.env.PROD) {
  Userpilot.initialize('NX-5916a30a');

  // Initialize version check service worker in production
  (async () => {
    try {
      await versionCheckService.initialize();
    } catch (err) {
      console.error('Failed to initialize version check service:', err);
    }
  })();
}

// Global handler for unhandled promise rejections (catches lazy route failures)
globalThis.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  const error = event.reason;
  const isChunkLoadError =
    error?.message?.includes('Failed to fetch dynamically imported module') ||
    error?.message?.includes('Importing a module script failed') ||
    error?.message?.includes('error loading dynamically imported module');

  if (isChunkLoadError) {
    console.error('Unhandled chunk load error, reloading page...', error);
    event.preventDefault();
    (globalThis as any).location.reload();
  }
});

setApiConfig({ baseUrl: import.meta.env.CTY_API_URL || new URL((globalThis as any).location.href)?.origin });
const agentsStreamEnv = import.meta.env.CTY_AGENTS_STREAM_URL;
if (agentsStreamEnv) {
  setAgentsStreamConfig({ baseUrl: String(agentsStreamEnv) });
}
const agenticAnalystEnv = import.meta.env.CTY_AGENTIC_ANALYST_URL;
if (agenticAnalystEnv) {
  setAgenticAnalystConfig({ baseUrl: String(agenticAnalystEnv) });
}
const voiceAgentEnv = import.meta.env.CTY_VOICE_AGENT_BASE_URL;
if (voiceAgentEnv) {
  const stHost = String(import.meta.env.CTY_WEBRTC_STUNNER_HOST ?? '').trim();
  const stUser = String(import.meta.env.CTY_WEBRTC_STUNNER_USER ?? '').trim();
  const stCred = String(import.meta.env.CTY_WEBRTC_STUNNER_CREDENTIAL ?? '').trim();
  setVoiceAgentConfig({
    baseUrl: String(voiceAgentEnv),
    ...(stHost && stUser && stCred
      ? { webrtcStunner: { host: stHost, username: stUser, credential: stCred } }
      : {}),
  });
}
const store = createStore();
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <StrictMode>
    <ChunkLoadErrorBoundary>
      <BrowserRouter>
        <Auth0ProviderWithHookInterceptor>
          <Provider store={store}>

            <App />
            {import.meta.env.PROD && <VersionUpdateNotification />}

          </Provider>
        </Auth0ProviderWithHookInterceptor>
      </BrowserRouter>
    </ChunkLoadErrorBoundary>
  </StrictMode>
);
